import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, Observable } from 'rxjs';
import { SearchResultsCommand } from '../../../core/commands/search-results.command';
import { WorksitesCommand } from '../../../core/commands/worksites.command';
import { Country } from '../../../core/models/country.model';
import { ListCompany } from '../../../core/models/list-company.model';
import { ListWorksite } from '../../../core/models/list-worksite.model';
import { SearchParams } from '../../../core/models/search-params.model';
import { SearchResult } from '../../../core/models/search-result.model';
import { WorksiteDetail } from '../../../core/models/worksite-detail.model';
import { SearchResultsQuery } from '../../../core/queries/search-results.query';
import { UtilitiesQuery } from '../../../core/queries/utilities.query';
import { WorksitesQuery } from '../../../core/queries/worksites.query';
import { SearchResultsRepository } from '../../../core/repositories/search-result.repository';

@UntilDestroy()
@Component({
  selector: 'lib-search-layout',
  templateUrl: './search-layout.component.html',
  styleUrls: ['./search-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchLayoutComponent implements OnInit {
  form: UntypedFormGroup;
  countries: Country[];
  searchResult$: Observable<SearchResult<ListCompany>>;
  searchParams: SearchParams = { number: '', name: '', city: '', country: '' };
  searchIsPressed = false;

  @Input() backToSearch: boolean;
  @Input() alreadySelectedWorksites: string[];
  @Input() alreadySelectedFinanciers: string[];
  @Input() isFinancierSearch: false;
  @Output() createWorksite = new EventEmitter<ListCompany>();
  @Output() createCompanyAndWorksite = new EventEmitter<void>();
  @Output() selectWorksite = new EventEmitter<WorksiteDetail>();

  constructor(
    private fb: UntypedFormBuilder,
    private utilitiesQuery: UtilitiesQuery,
    private searchResultsQuery: SearchResultsQuery,
    private searchResultsCommand: SearchResultsCommand,
    private searchResultsRepository: SearchResultsRepository,
    private worksitesQuery: WorksitesQuery,
    private worksitesCommand: WorksitesCommand,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit() {
    this.countries = this.utilitiesQuery.getCountries(true);
    this.searchResult$ = this.searchResultsQuery.selectSearchResult();
    this.form = this.buildSearchForm();
    this.searchResultsCommand.unsetActive();

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe((language) => {
        this.utilitiesQuery.loadUtilities(language);
        this.countries = this.utilitiesQuery.getCountries(true);
      });
  }

  onCreateWorksite(company: ListCompany) {
    this.searchResultsCommand.updateIsSearching(true);
    this.searchResultsCommand.setActive(company.providerId);

    const activeCompany = { ...this.searchResultsRepository.getActive() };
    this.worksitesQuery
      .search(
        undefined,
        undefined,
        undefined,
        this.form.get('country').value,
        undefined,
        activeCompany.providerId,
      )
      .pipe(
        finalize(() => {
          this.searchResultsCommand.updateIsSearching(false);
          this.createWorksite.emit(company);
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        for (const company of result.result) {
          if (activeCompany.providerId === company.providerId) {
            const activeWorksites = [...activeCompany.worksites];
            const identicalWorksites = activeCompany.worksites
              .flatMap((w) => w.identicalWorksites)
              .map((p) => p.providerId);

            for (const worksite of company.worksites) {
              if (
                !activeWorksites.some(
                  (w) =>
                    w.providerId === worksite.providerId ||
                    identicalWorksites.includes(worksite.providerId),
                )
              ) {
                const country = this.countries.find(
                  (country) => country.value === worksite.country,
                );
                worksite.country = country ? country.label : '';
                activeWorksites.push(worksite);
              }
              activeCompany.worksites = activeWorksites;
            }
          }
        }

        this.searchResultsCommand.updateActiveCompany(activeCompany);
      });
  }

  onCreateCompanyAndWorksite(event) {
    this.createCompanyAndWorksite.emit(event);
  }

  onSelectWorksite(worksite: ListWorksite): void {
    this.worksitesCommand
      .selectWorksite(worksite.provider, worksite.providerId)
      .pipe(untilDestroyed(this))
      .subscribe((selectedWorksite) => {
        if (selectedWorksite) {
          this.searchResultsCommand.set(undefined);
          this.selectWorksite.emit(selectedWorksite);
        } else {
          this.searchResultsRepository.setWorksiteIsUnavailable(
            worksite.providerId,
          );
        }
      });
  }

  onSearch() {
    this.searchIsPressed = true;
    this.searchResultsCommand.set(undefined);
    this.searchParams.number = this.form.get('number').value;
    this.searchParams.name = this.form.get('name').value;
    this.worksitesQuery
      .search(
        this.form.get('number').value,
        this.form.get('name').value,
        this.form.get('city').value,
        this.form.get('country').value,
      )
      .pipe(
        finalize(() => this.searchResultsCommand.updateIsSearching(false)),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        for (const company of result.result) {
          for (const worksite of company.worksites) {
            const country = this.countries.find(
              (country) => country.value === worksite.country,
            );
            worksite.country = country ? country.label : '';
          }
        }

        this.searchResultsCommand.set(result);
        this.searchResultsCommand.updateSearchParams(this.form.value);

        for (const company of result.result) {
          if (
            this.searchParams.name.toLowerCase() ==
              company.name.toLowerCase() ||
            this.searchParams.number
          ) {
            this.searchResultsCommand.updateShowAllWorksitesParameter(
              company.providerId,
              false,
            );
          }
        }
      });
  }

  buildSearchForm() {
    return this.fb.group({
      number: [''],
      name: [''],
      city: [''],
      country: ['SE'],
    });
  }
}
